import IssueCard from "./IssueCard";
import { SeverityBadge } from "./SeverityBadge";
import { MarketTierPill } from "./ConfidenceBadge";
import PremiumIntelligence from "./PremiumIntelligence";
import ReportControls, { applyReportFilter } from "./ReportControls";
import ReportTopPriorities from "./ReportTopPriorities";
import ReportWhatNotToPanic from "./ReportWhatNotToPanic";
import DealRiskSnapshot from "./DealRiskSnapshot";
import ReadinessScore from "./ReadinessScore";
import ReportTOC from "./ReportTOC";
import NegotiationSummaryBuilder from "./NegotiationSummaryBuilder";
import { CheckCircle2, AlertTriangle, FileText, Wrench, MapPin, ListChecks } from "lucide-react";
import { useState, useEffect } from "react";
import LocalizeReportDialog from "@/components/dashboard/LocalizeReportDialog";
import MarketTierBadge from "@/components/tools/MarketTierBadge";
import { fetchReportUserState, patchIssueState } from "@/lib/api";
import { track } from "@/lib/analytics";

function Money({ value }) {
    return (
        <span className="font-mono-bl tnum">
            {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                maximumFractionDigits: 0,
            }).format(value || 0)}
        </span>
    );
}

export default function ReportFull({ report, ownedTier, isSample = false, onGetQuotes = null }) {
    const data = report?.full_summary;
    const [localizing, setLocalizing] = useState(false);
    const [stateByIssue, setStateByIssue] = useState({});
    const [controls, setControls] = useState({ search: "", filter: "all", collapsed: false });
    const [summaryOpen, setSummaryOpen] = useState(false);

    const canManageState = !!ownedTier && !isSample;

    // Load saved per-issue state once for owners of non-sample reports.
    useEffect(() => {
        if (!canManageState || !report?.report_id) return;
        fetchReportUserState(report.report_id)
            .then((s) => setStateByIssue(s || {}))
            .catch(() => {});
    }, [canManageState, report?.report_id]);

    const handleIssueStateChange = async (issueId, patch) => {
        if (!canManageState || !report?.report_id) return;
        // Optimistic update
        setStateByIssue((prev) => {
            const cur = prev[issueId] || {};
            const next = { ...cur };
            if (patch.reviewed !== undefined) {
                next.reviewed_at = patch.reviewed ? new Date().toISOString() : null;
            }
            if (patch.saved_for_later !== undefined) next.saved_for_later = !!patch.saved_for_later;
            if (patch.hidden_by_user !== undefined) next.hidden_by_user = !!patch.hidden_by_user;
            return { ...prev, [issueId]: next };
        });
        try {
            await patchIssueState(report.report_id, issueId, patch);
            if (patch.reviewed) track("issue_marked_reviewed", { report_id: report.report_id, issue_id: issueId });
            if (patch.saved_for_later) track("issue_saved_for_later", { report_id: report.report_id, issue_id: issueId });
            if (patch.hidden_by_user) track("issue_hidden", { report_id: report.report_id, issue_id: issueId });
        } catch {
            // best-effort; we'll fall back to last server state on next reload
        }
    };

    if (!data) return null;
    const allIssues = data.issues || [];
    // Apply search/filter ONCE to the full list — then re-group into timeline buckets.
    const filteredIssues = canManageState
        ? applyReportFilter(allIssues, stateByIssue, controls)
        : allIssues;
    const groups = {
        immediate: filteredIssues.filter((i) => i.timeline === "immediate"),
        plan: filteredIssues.filter((i) => i.timeline === "3_to_12_months"),
        longterm: filteredIssues.filter((i) => i.timeline === "long_term"),
    };
    // Only owners (with ownedTier) of a non-sample report can localize.
    const canLocalize =
        !isSample &&
        !!ownedTier &&
        !!report.upload_id &&
        (!report.zip_code || (data.market_profile && data.market_profile.known === false));

    const isPremium = ownedTier === "closing_strategy" || report?.tier === "closing_strategy";
    const cs = data.closing_strategy || {};
    const hasTopPriorities = Array.isArray(data.top_5_priorities) && data.top_5_priorities.length > 0;
    const hasWhatNotToPanic = Array.isArray(data.what_not_to_panic_about) && data.what_not_to_panic_about.length > 0;
    const hasDealRisk = isPremium && cs.deal_risk_snapshot && typeof cs.deal_risk_snapshot === "object";
    const hasReadiness = !!(isPremium || cs.questions_for_seller || cs.questions_for_contractor);

    // Compute available TOC sections based on what's actually present.
    const tocIds = [
        "summary",
        hasTopPriorities && "top-priorities",
        hasReadiness && "readiness",
        hasDealRisk && "deal-risk",
        data.exposure_breakdown && "exposure",
        canManageState && allIssues.length > 1 && "controls",
        groups.immediate.length + groups.plan.length + groups.longterm.length > 0 && "issues",
        (hasWhatNotToPanic || data.lower_priority_items?.length > 0) && "not-panic",
        isPremium && "premium",
        data.specialist_recommendations?.length > 0 && "specialists",
        data.questions_to_ask?.length > 0 && "questions",
    ].filter(Boolean);

    return (
        <div className="space-y-16 md:space-y-24" data-testid="report-full">
            {/* Sticky table of contents */}
            <ReportTOC availableIds={tocIds} />

            {/* Header / meta */}
            <header className="bl-card p-6 sm:p-8 md:p-12" data-toc-section="summary">
                <div className="flex items-center gap-2 bl-eyebrow w-fit">
                    <FileText className="w-3.5 h-3.5" /> Full Report
                </div>
                <h1 className="mt-4 font-display font-semibold text-[1.75rem] sm:text-3xl md:text-5xl tracking-tight leading-[1.1] sm:leading-[1.05] text-bl">
                    {data.property_address || "Inspection Analysis"}
                </h1>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-bl-muted">
                    {data.inspector_name && (
                        <span className="font-mono-bl">
                            Inspector · {data.inspector_name}
                        </span>
                    )}
                    {data.inspection_date && (
                        <span className="font-mono-bl">
                            Date · {data.inspection_date}
                        </span>
                    )}
                    <span className="font-mono-bl">
                        Issues identified · {allIssues.length}
                    </span>
                </div>

                {/* Executive summary */}
                <p className="mt-8 text-lg md:text-xl leading-relaxed text-bl max-w-3xl">
                    <span className="font-italic-accent text-bl-primary mr-1">
                        Executive Summary —
                    </span>{" "}
                    {data.executive_summary}
                </p>

                {/* Totals — exposure breakdown (Premium-aware) */}
                {data.exposure_breakdown ? (
                    <div className="mt-8" data-testid="exposure-breakdown" data-toc-section="exposure">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="bl-card p-5 border-l-2 border-l-[var(--bl-rust)]/40">
                                <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-1.5">
                                    Priority Repair Exposure
                                </div>
                                <div className="text-2xl font-mono-bl tnum text-bl">
                                    <Money value={data.exposure_breakdown.priority?.low} /> – <Money value={data.exposure_breakdown.priority?.high} />
                                </div>
                            </div>
                            <div className="bl-card p-5 border-l-2 border-l-bl-primary/30">
                                <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-1.5">
                                    Deferred Exposure
                                </div>
                                <div className="text-2xl font-mono-bl tnum text-bl">
                                    <Money value={data.exposure_breakdown.deferred?.low} /> – <Money value={data.exposure_breakdown.deferred?.high} />
                                </div>
                            </div>
                            <div className="bl-card p-5 border-l-2 border-l-bl-soft">
                                <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-1.5">
                                    Optional / Long-Term
                                </div>
                                <div className="text-2xl font-mono-bl tnum text-bl">
                                    <Money value={data.exposure_breakdown.optional?.low} /> – <Money value={data.exposure_breakdown.optional?.high} />
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 bl-card p-5 bg-bl-teal-tint/30">
                            <div className="flex items-baseline justify-between flex-wrap gap-3">
                                <div>
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                                        Total Planning Exposure
                                    </div>
                                    <div className="mt-1 text-[11px] text-bl-muted">
                                        De-duped across overlapping issues — not a guaranteed cost.
                                    </div>
                                </div>
                                <div className="font-display text-3xl text-bl font-mono-bl tnum">
                                    <Money value={data.exposure_breakdown.total_planning_exposure?.low} /> – <Money value={data.exposure_breakdown.total_planning_exposure?.high} />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bl-card p-5">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-1.5">
                                Estimated Professional Repair Exposure (low)
                            </div>
                            <div className="text-2xl">
                                <Money value={data.repair_exposure_total_usd?.low} />
                            </div>
                        </div>
                        <div className="bl-card p-5">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-1.5">
                                Estimated Professional Repair Exposure (high)
                            </div>
                            <div className="text-2xl">
                                <Money value={data.repair_exposure_total_usd?.high} />
                            </div>
                        </div>
                        <div className="bl-card p-5">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-1.5">
                                Tier
                            </div>
                            <div className="text-base text-bl capitalize">
                                {(ownedTier || report?.tier || "preview").replace("_", " ")}
                            </div>
                        </div>
                    </div>
                )}
                {data.market_profile && (
                    <>
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                            <MarketTierPill profile={data.market_profile} />
                            {canLocalize && data.market_profile.known === false && (
                                <button
                                    type="button"
                                    onClick={() => setLocalizing(true)}
                                    className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-bl-primary hover:underline"
                                    data-testid="report-localize-improve-btn"
                                    title="This ZIP isn't in our local-cost map yet — try a nearby ZIP for sharper localization"
                                >
                                    <MapPin className="w-2.5 h-2.5" />
                                    Update ZIP
                                </button>
                            )}
                        </div>
                        <p className="mt-3 text-xs text-bl-muted leading-relaxed">
                            Localized to <span className="text-bl">{data.market_profile.name}</span>{" "}
                            (labor {data.market_profile.labor_mult}× · material{" "}
                            {data.market_profile.material_mult}×). Numbers are broad{" "}
                            <span className="text-bl">contractor-level planning ranges</span> that
                            reflect mobilization, overhead, minimum-job pricing, and permits where
                            relevant — not DIY material-only estimates.
                        </p>
                    </>
                )}
                {canLocalize && !data.market_profile && (
                    <div
                        className="mt-4 bl-card p-4 flex items-center justify-between gap-3 bg-bl-teal-tint border-bl-primary/20"
                        data-testid="report-localize-cta"
                    >
                        <div className="flex items-start gap-2 min-w-0">
                            <MapPin className="w-4 h-4 text-bl-primary mt-0.5 shrink-0" />
                            <p className="text-sm text-bl leading-relaxed">
                                Cost ranges below are using <span className="text-bl">national-average</span> pricing.
                                <span className="text-bl-muted"> Add the property ZIP to localize labor, materials, and contractor minimums to this market — free, instant.</span>
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setLocalizing(true)}
                            className="bl-btn-primary text-sm shrink-0"
                            data-testid="report-localize-cta-btn"
                        >
                            <MapPin className="w-3.5 h-3.5" />
                            Add ZIP
                        </button>
                    </div>
                )}
            </header>

            <LocalizeReportDialog
                report={localizing ? { ...report, upload_id: report.upload_id, property_address: report.property_address } : null}
                onClose={() => setLocalizing(false)}
                onApplied={() => {
                    setLocalizing(false);
                    // Force a fresh fetch so the new full_summary + market_profile renders.
                    if (typeof window !== "undefined") {
                        window.location.reload();
                    }
                }}
            />

            {/* In-report search + filters + collapse-all — owners only */}
            {canManageState && allIssues.length > 1 && (
                <div data-toc-section="controls">
                    <ReportControls
                        issues={allIssues}
                        stateByIssue={stateByIssue}
                        onChange={setControls}
                    />
                </div>
            )}

            {/* Top 5 Priorities — calm, scannable, AI-once */}
            {hasTopPriorities && (
                <div data-toc-section="top-priorities">
                    <ReportTopPriorities priorities={data.top_5_priorities} />
                </div>
            )}

            {/* Readiness Score — deterministic compute, no AI */}
            {hasReadiness && (
                <div data-toc-section="readiness">
                    <ReadinessScore data={data} isPremium={isPremium} />
                </div>
            )}

            {/* Premium Deal Risk Snapshot */}
            {hasDealRisk && (
                <div data-toc-section="deal-risk">
                    <DealRiskSnapshot snapshot={cs.deal_risk_snapshot} />
                </div>
            )}

            {/* Highest priority concerns */}
            {data.highest_priority_concerns?.length > 0 && !hasTopPriorities && (
                <section data-testid="section-highest-priority" data-reveal>
                    <div className="flex items-baseline justify-between mb-6">
                        <div>
                            <div className="bl-eyebrow">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                Highest Priority
                            </div>
                            <h2 className="mt-3 text-3xl md:text-4xl font-display font-semibold tracking-tight text-bl">
                                Where to focus first
                            </h2>
                        </div>
                    </div>
                    <div className="grid gap-5 md:grid-cols-2">
                        {data.highest_priority_concerns.map((c, i) => (
                            <div key={i} className="bl-card p-6">
                                <SeverityBadge severity={c.severity} />
                                <h3 className="mt-3 font-display font-semibold text-bl text-lg">
                                    {c.title}
                                </h3>
                                <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                                    {c.category}
                                </p>
                                <p className="mt-3 text-[15px] text-bl leading-relaxed">
                                    {c.why_it_matters}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Negotiation opportunities */}
            {data.negotiation_opportunities?.length > 0 && (
                <section data-testid="section-negotiation" data-reveal>
                    <div className="bl-eyebrow">Negotiation Leverage</div>
                    <h2 className="mt-3 text-3xl md:text-4xl font-display font-semibold tracking-tight text-bl">
                        Where leverage lives
                    </h2>
                    <p className="mt-3 max-w-2xl text-bl-muted">
                        These items often justify a closing credit, a price reduction, or
                        a documented repair. Pair each with a written estimate where
                        possible.
                    </p>
                    <div className="mt-8 space-y-4">
                        {data.negotiation_opportunities.map((n, i) => (
                            <div key={i} className="bl-card p-6 md:p-8">
                                <div className="text-[10px] tracking-[0.2em] uppercase text-bl-primary font-medium">
                                    Opportunity #{i + 1}
                                </div>
                                <h3 className="mt-2 text-xl font-display font-semibold text-bl">
                                    {n.title}
                                </h3>
                                <p className="mt-3 text-bl leading-relaxed">
                                    {n.leverage_angle}
                                </p>
                                <div className="mt-4 bg-bl-teal-tint rounded-xl p-4">
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-primary font-medium mb-1.5">
                                        Suggested ask
                                    </div>
                                    <p className="text-[15px] text-bl">
                                        {n.suggested_ask}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Timeline grouping */}
            <div data-toc-section="issues">
            {report?.zip_code && (
                <div
                    className="mb-8"
                    data-testid="report-market-tier-banner"
                >
                    <MarketTierBadge zipCode={report.zip_code} />
                </div>
            )}
            {[
                ["immediate", "Immediate Attention", "Safety, water, structural, near-term"],
                ["plan", "3–12 Month Planning", "Budget and follow up soon"],
                ["longterm", "Long-Term Monitoring", "Watch, maintain, revisit later"],
            ].map(([key, title, sub]) =>
                groups[key].length > 0 ? (
                    <section key={key} data-testid={`section-timeline-${key}`} className="mb-16 md:mb-24" data-reveal>
                        <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
                            <div>
                                <div className="bl-eyebrow">Timeline</div>
                                <h2 className="mt-3 text-2xl md:text-3xl font-display font-semibold tracking-tight text-bl">
                                    {title}
                                </h2>
                            </div>
                            <p className="text-sm text-bl-muted">{sub}</p>
                        </div>
                        <div className="grid gap-5 md:grid-cols-2">
                            {groups[key].map((iss) => (
                                <IssueCard
                                    key={iss.issue_id}
                                    issue={iss}
                                    ownedTier={ownedTier}
                                    reportId={data.report_id || report?.report_id}
                                    uploadId={report?.upload_id}
                                    userState={stateByIssue[iss.issue_id]}
                                    onStateChange={canManageState ? handleIssueStateChange : null}
                                    onGetQuote={canManageState && onGetQuotes ? () => onGetQuotes() : null}
                                    defaultCollapsed={controls.collapsed}
                                />
                            ))}
                        </div>
                    </section>
                ) : null,
            )}
            </div>

            {/* "What not to panic about" — preferred, calmer copy. Falls back
                to legacy lower_priority_items for pre-v2.9 reports. */}
            {hasWhatNotToPanic ? (
                <div data-toc-section="not-panic">
                    <ReportWhatNotToPanic items={data.what_not_to_panic_about} />
                </div>
            ) : data.lower_priority_items?.length > 0 ? (
                <section data-testid="section-lower-priority" data-toc-section="not-panic" data-reveal>
                    <div className="bl-eyebrow">Lower Priority</div>
                    <h2 className="mt-3 text-2xl md:text-3xl font-display font-semibold tracking-tight text-bl">
                        Watch list
                    </h2>
                    <div className="mt-6 grid gap-3 md:grid-cols-2">
                        {data.lower_priority_items.map((it, i) => (
                            <div key={i} className="bl-card p-5">
                                <div className="flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                    {it.category}
                                </div>
                                <h3 className="mt-1 text-base font-medium text-bl">
                                    {it.title}
                                </h3>
                                <p className="mt-2 text-sm text-bl-muted">{it.note}</p>
                            </div>
                        ))}
                    </div>
                </section>
            ) : null}

            {/* Premium-only intelligence — only rendered for closing_strategy tier */}
            {isPremium && (
                <div data-toc-section="premium" className="space-y-6">
                    {canManageState && (
                        <div
                            className="bl-card p-5 md:p-6 flex items-start justify-between gap-4 flex-wrap bg-bl-teal-tint/30 border-bl-primary/20"
                            data-testid="negsum-cta-wrap"
                        >
                            <div className="min-w-0 flex-1">
                                <div className="bl-eyebrow">
                                    <ListChecks className="w-3.5 h-3.5" /> Build your brief
                                </div>
                                <h3 className="mt-2 font-display font-semibold text-bl text-lg">
                                    Turn this into an agent-ready negotiation summary.
                                </h3>
                                <p className="mt-1.5 text-sm text-bl-muted leading-relaxed max-w-xl">
                                    Pick the issues that matter to you, reorder them,
                                    add seller / contractor questions, then copy or
                                    print a clean brief. It builds entirely from
                                    your stored report — nothing else is sent
                                    anywhere.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    track("negotiation_summary_opened", { report_id: report?.report_id });
                                    setSummaryOpen(true);
                                }}
                                className="bl-btn-primary flex-shrink-0"
                                data-testid="negsum-open-cta"
                            >
                                <ListChecks className="w-3.5 h-3.5" /> Build summary
                            </button>
                        </div>
                    )}
                    <PremiumIntelligence data={data} />
                </div>
            )}

            {/* Builder modal — Premium owners only */}
            <NegotiationSummaryBuilder
                open={summaryOpen}
                onClose={() => setSummaryOpen(false)}
                report={report}
                stateByIssue={stateByIssue}
                onStateChange={handleIssueStateChange}
            />

            {/* Specialist recs + follow-ups */}
            <section className="grid gap-6 md:grid-cols-2" data-testid="section-followups" data-toc-section="specialists" data-reveal>
                {data.recommended_followups?.length > 0 && (
                    <div className="bl-card p-6 md:p-8">
                        <div className="bl-eyebrow">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Recommended Follow-ups
                        </div>
                        <ul className="mt-5 space-y-3">
                            {data.recommended_followups.map((f, i) => (
                                <li key={i} className="flex gap-3 text-[15px] text-bl">
                                    <span className="text-bl-primary mt-1">·</span>
                                    <span className="leading-relaxed">{f}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {data.specialist_recommendations?.length > 0 && (
                    <div className="bl-card p-6 md:p-8">
                        <div className="bl-eyebrow">
                            <Wrench className="w-3.5 h-3.5" /> Specialists to consider
                        </div>
                        <div className="mt-5 flex flex-wrap gap-2">
                            {data.specialist_recommendations.map((s, i) => (
                                <span key={i} className="bl-badge bl-badge-neutral">
                                    {s}
                                </span>
                            ))}
                        </div>
                        {data.questions_to_ask?.length > 0 && (
                            <div className="mt-6">
                                <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                                    Questions to ask
                                </div>
                                <ul className="mt-3 space-y-2.5">
                                    {data.questions_to_ask.map((q, i) => (
                                        <li
                                            key={i}
                                            className="text-[15px] text-bl leading-relaxed flex gap-2"
                                        >
                                            <span className="text-bl-primary">·</span>
                                            <span>{q}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </section>

            {/* Resources */}
            <section
                className="bl-card p-6 md:p-8 bg-bl-surface"
                data-testid="section-resources"
                data-reveal
            >
                <div className="bl-eyebrow">Additional Planning Resources</div>
                <p className="mt-4 text-bl leading-relaxed max-w-2xl">
                    {data.additional_resources_note ||
                        "Some buyers explore additional protection or financing options while planning for repairs and ongoing home maintenance."}
                </p>
                <div className="mt-5 grid gap-3 md:grid-cols-3">
                    {[
                        ["Home warranty resources", "/contact"],
                        ["HELOC & financing", "/contact"],
                        ["Repair planning", "/contact"],
                    ].map(([label, to]) => (
                        <a
                            key={label}
                            href={to}
                            className="bl-card p-4 hover:border-bl-primary transition-colors text-sm text-bl"
                            data-testid={`resource-${label.toLowerCase().replace(/\s+/g, "-")}-link`}
                        >
                            {label} →
                        </a>
                    ))}
                </div>
                <p className="mt-5 text-xs text-bl-muted">
                    Buyer&apos;s Leverage may receive compensation from some resource
                    providers. These resources are optional and provided for informational
                    convenience only.
                </p>
            </section>
        </div>
    );
}

export function ReportPreview({ report, onUnlock }) {
    const p = report?.preview_summary;
    if (!p) return null;
    const exposure = p.estimated_planning_range_usd;
    const hasRange = exposure && exposure.low > 0 && exposure.high > exposure.low;
    const fmt = (n) => `$${Math.round(n).toLocaleString("en-US")}`;
    const scrollToPaywall = () => {
        if (typeof onUnlock === "function") {
            onUnlock();
            return;
        }
        const el = document.querySelector('[data-testid="paywall"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    return (
        <div className="space-y-10" data-testid="report-preview">
            <header className="bl-card p-6 sm:p-8 md:p-12">
                <div className="bl-eyebrow">Preview</div>
                <h1 className="mt-4 font-display font-semibold text-[1.75rem] sm:text-3xl md:text-5xl tracking-tight leading-[1.1] sm:leading-[1.05] text-bl">
                    {p.property_address || "Inspection Preview"}
                </h1>
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-bl-muted">
                    {p.inspector_name && (
                        <span className="font-mono-bl">{p.inspector_name}</span>
                    )}
                    {p.inspection_date && (
                        <span className="font-mono-bl">{p.inspection_date}</span>
                    )}
                </div>

                {/* Headline value banner — shown when the rule-based engine
                   produced a meaningful planning range from the preview. */}
                {hasRange && (
                    <div
                        className="mt-6 rounded-xl bg-[var(--bl-teal-tint)] border-2 border-bl-primary/30 p-5 sm:p-6"
                        data-testid="preview-value-banner"
                    >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="min-w-0">
                                <div className="text-[10px] tracking-[0.18em] uppercase text-bl-primary font-semibold">
                                    What we found in your inspection
                                </div>
                                <p className="mt-2 text-bl text-base sm:text-lg leading-relaxed">
                                    We found potential negotiation opportunities and
                                    repair planning insights valued at{" "}
                                    <span
                                        className="font-mono-bl tnum font-semibold text-bl-primary whitespace-nowrap"
                                        data-testid="preview-exposure-range"
                                    >
                                        {fmt(exposure.low)} – {fmt(exposure.high)}
                                    </span>
                                    .
                                </p>
                                <p className="mt-2 text-xs text-bl-muted leading-relaxed">
                                    Planning-range estimate based on the categories detected
                                    and typical contractor pricing. The full report breaks
                                    each finding into severity-scored, prioritized cost ranges.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={scrollToPaywall}
                                className="bl-btn-primary whitespace-nowrap shrink-0"
                                data-testid="preview-unlock-cta"
                            >
                                Unlock full report →
                            </button>
                        </div>
                    </div>
                )}

                <p className="mt-6 text-lg leading-relaxed text-bl max-w-3xl">
                    <span className="font-italic-accent text-bl-primary mr-1">
                        Executive teaser —
                    </span>
                    {p.executive_teaser}
                </p>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bl-card p-4">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                            Issues found
                        </div>
                        <div className="mt-1 text-2xl font-mono-bl tnum">
                            {p.issue_count_estimate || 0}
                        </div>
                    </div>
                    <div className="bl-card p-4 col-span-1 sm:col-span-3">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                            Categories detected
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {(p.categories_detected || []).map((c) => (
                                <span key={c} className="bl-badge bl-badge-neutral">
                                    {c}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </header>

            <section data-reveal>
                <div className="bl-eyebrow">Highlights</div>
                <h2 className="mt-3 text-2xl md:text-3xl font-display font-semibold text-bl">
                    A sample of what we&apos;ve found
                </h2>
                <div className="mt-6 grid gap-5 md:grid-cols-2">
                    {(p.highlights || []).map((h, i) => (
                        <div key={i} className="bl-card p-6">
                            <SeverityBadge severity={h.severity} />
                            <h3 className="mt-3 font-display font-semibold text-bl">
                                {h.title}
                            </h3>
                            <p className="mt-1 text-sm text-bl-muted">{h.category}</p>
                            <p className="mt-3 text-[15px] text-bl leading-relaxed">
                                {h.teaser}
                            </p>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
